import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { PageTitle } from '../../_metronic/layout/core'
import { useAuth } from '../../app/modules/auth'
import axios from 'axios'
import { BASE_API_URL } from '../core/_requests'
import { DateTime } from 'luxon'

const API_URL = `${BASE_API_URL}/admin/api/v1`

// Allowed emails for web leads access (same as billing)
const ALLOWED_EMAILS: string[] = ['chris@cohoat.tech', 'jnalls@mogulclients.com']

interface WebLead {
  uuid: string
  name: string
  email: string
  company: string | null
  phone: string | null
  lead_type: string | null
  message: string | null
  source: string
  source_url: string | null
  status: string
  inserted_at: string
}

interface WebLeadsData {
  web_leads: WebLead[]
  total_count: number
  limit: number
  offset: number
}

interface WebLeadsResponse {
  data: WebLeadsData
}

function WebLeadsPage() {
  const { currentUser, auth } = useAuth()
  const [webLeads, setWebLeads] = useState<WebLead[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if user has access
  const hasAccess = currentUser?.email && ALLOWED_EMAILS.includes(currentUser.email)

  useEffect(() => {
    if (!hasAccess || !auth?.api_token) return

    const fetchWebLeads = async () => {
      try {
        setLoading(true)
        const response = await axios.get<WebLeadsResponse>(
          `${API_URL}/web-leads`,
          {
            headers: { Authorization: `Bearer ${auth.api_token}` },
          }
        )
        setWebLeads(response.data.data?.web_leads || [])
        setTotalCount(response.data.data?.total_count || 0)
        setError(null)
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch web leads'
        setError(errorMessage)
      } finally {
        setLoading(false)
      }
    }

    fetchWebLeads()
  }, [hasAccess, auth?.api_token])

  const updateStatus = async (uuid: string, status: string) => {
    if (!auth?.api_token) return

    try {
      await axios.put(
        `${API_URL}/web-leads/${uuid}/status`,
        { status },
        {
          headers: { Authorization: `Bearer ${auth.api_token}` },
        }
      )
      // Update local state
      setWebLeads(prev =>
        prev.map(lead =>
          lead.uuid === uuid ? { ...lead, status } : lead
        )
      )
    } catch (err) {
      console.error('Failed to update status:', err)
    }
  }

  const formatDate = (dateString: string) => {
    return DateTime.fromISO(dateString).toFormat('MMM d, yyyy h:mm a')
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'new':
        return 'badge-light-primary'
      case 'contacted':
        return 'badge-light-info'
      case 'qualified':
        return 'badge-light-success'
      case 'closed':
        return 'badge-light-dark'
      default:
        return 'badge-light-secondary'
    }
  }

  const getLeadTypeBadge = (leadType: string | null) => {
    switch (leadType) {
      case 'funeral-home':
        return <span className="badge badge-light-primary">Funeral Home</span>
      case 'marketing-agency':
        return <span className="badge badge-light-info">Marketing Agency</span>
      default:
        return leadType ? <span className="badge badge-light-secondary">{leadType}</span> : null
    }
  }

  // Redirect if no access
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Web Leads</PageTitle>

      <div className="card">
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <h3 className="card-label">Marketing Site Contact Form Submissions</h3>
          </div>
          <div className="card-toolbar">
            <span className="badge badge-light-info fs-7">
              {totalCount} total leads
            </span>
          </div>
        </div>

        <div className="card-body py-4">
          {loading ? (
            <div className="d-flex justify-content-center py-10">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : !webLeads || webLeads.length === 0 ? (
            <div className="text-center py-10 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-3"></i>
              No web leads yet
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr className="fw-bold text-muted">
                    <th className="min-w-150px">Contact</th>
                    <th className="min-w-120px">Company</th>
                    <th className="min-w-100px">Type</th>
                    <th className="min-w-200px">Message</th>
                    <th className="min-w-100px">Status</th>
                    <th className="min-w-120px">Date</th>
                    <th className="min-w-100px text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {webLeads.map((lead) => (
                    <tr key={lead.uuid}>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="fw-bold text-gray-800">{lead.name}</span>
                          <a href={`mailto:${lead.email}`} className="text-muted text-hover-primary fs-7">
                            {lead.email}
                          </a>
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="text-muted text-hover-primary fs-7">
                              {lead.phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="text-gray-700">{lead.company || '-'}</span>
                      </td>
                      <td>{getLeadTypeBadge(lead.lead_type)}</td>
                      <td>
                        <span className="text-gray-600 fs-7" style={{ maxWidth: '300px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lead.message || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(lead.status)}`}>
                          {lead.status}
                        </span>
                      </td>
                      <td>
                        <span className="text-gray-600 fs-7">{formatDate(lead.inserted_at)}</span>
                      </td>
                      <td className="text-end">
                        <div className="dropdown">
                          <button
                            className="btn btn-sm btn-light btn-active-light-primary"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            {lead.status}
                            <i className="bi bi-chevron-down ms-2"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button className="dropdown-item" onClick={() => updateStatus(lead.uuid, 'new')}>
                                New
                              </button>
                            </li>
                            <li>
                              <button className="dropdown-item" onClick={() => updateStatus(lead.uuid, 'contacted')}>
                                Contacted
                              </button>
                            </li>
                            <li>
                              <button className="dropdown-item" onClick={() => updateStatus(lead.uuid, 'qualified')}>
                                Qualified
                              </button>
                            </li>
                            <li>
                              <button className="dropdown-item" onClick={() => updateStatus(lead.uuid, 'closed')}>
                                Closed
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default WebLeadsPage
